// src/lib/applicationInsights.ts
import {
  ApplicationInsights,
  ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

export const reactPlugin = new ReactPlugin();

const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as
  | string
  | undefined;

// VITE_APP_ENV is set per deploy workflow (staging/production). Falls back to
// Vite's MODE for local dev — `vite build` reports MODE='production' for BOTH
// staging and prod, so the explicit env var is what disambiguates deploys.
const environment =
  (import.meta.env.VITE_APP_ENV as string | undefined) ?? import.meta.env.MODE;
const appVersion =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown';

// Property keys that must never reach Azure (auth tokens, cookies, secrets).
const SENSITIVE_KEY = /authorization|cookie|token|secret|password|bearer/i;

export const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    extensions: [reactPlugin],

    enableAutoRouteTracking: true,
    // Cross-origin correlation injects `request-id` / `traceparent` headers into
    // cross-origin API calls, which triggers a CORS preflight. Keep this OFF
    // until the backend adds those to CORS_ALLOW_HEADERS (django-cors-headers),
    // otherwise the preflight is rejected and ALL API calls fail with a CORS error.
    enableCorsCorrelation: false,

    // Header tracking is OFF: the API client attaches `Authorization: Bearer
    // <token>`, and capturing request/response headers would store those
    // tokens in Azure.
    enableRequestHeaderTracking: false,
    enableResponseHeaderTracking: false,

    autoTrackPageVisitTime: true,
    disableFetchTracking: false,
    disableAjaxTracking: false,

    // Adjust this down if production telemetry volume gets too high.
    samplingPercentage: 100,
  },
});

// Defense-in-depth scrub + environment/release tagging on every telemetry item.
const telemetryInitializer = (item: ITelemetryItem): boolean | void => {
  // Identify this app in the portal.
  item.tags = item.tags ?? {};
  item.tags['ai.cloud.role'] = 'odin-client-portal-frontend';

  // Stamp environment + release so dev noise can be filtered and regressions
  // tied to deploys.
  item.data = item.data ?? {};
  item.data.environment = environment;
  item.data.appVersion = appVersion;

  // Redact anything sensitive that slipped into custom properties.
  const props = item.baseData?.properties as Record<string, unknown> | undefined;
  if (props) {
    for (const key of Object.keys(props)) {
      if (SENSITIVE_KEY.test(key)) props[key] = '[REDACTED]';
    }
  }
};

export const initializeApplicationInsights = () => {
  if (!connectionString) {
    console.warn('Application Insights connection string is missing.');
    return;
  }

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(telemetryInitializer);
  appInsights.trackPageView();
};

/** Correlate telemetry to a user by id (no PII). Call on login / session restore. */
export const setTelemetryUser = (userId: string | number) => {
  if (!connectionString || userId == null) return;
  appInsights.setAuthenticatedUserContext(String(userId), undefined, true);
};

/** Drop user correlation. Call on logout / session expiry. */
export const clearTelemetryUser = () => {
  if (!connectionString) return;
  appInsights.clearAuthenticatedUserContext();
};
